<template>
  <b-field
    :label="label"
    class="date-range-picker"
    :class="{ 'date-range-picker--no-presets': !showPresets }"
  >
    <b-dropdown
      ref="dropdown"
      aria-role="dialog"
      expanded
      :position="dropdownPosition"
      :close-on-click="false"
      trap-focus
      @active-change="onActiveChange"
    >
      <template #trigger>
        <b-tooltip
          class="date-range-picker__tooltip"
          position="is-top"
          multilined
          :label="triggerHint || ''"
          :active="Boolean(triggerHint)"
        >
          <div
            class="control date-range-picker__control has-icons-left"
            :class="{ 'has-icons-right': Boolean(displayValue) }"
            @mouseenter="refreshTriggerHint"
          >
            <button type="button" class="button is-fullwidth date-range-picker__trigger">
              <span
                ref="triggerLabel"
                class="date-range-picker__trigger-label"
                :class="{ 'is-placeholder': !displayValue }"
                >{{ displayValue || placeholder }}</span
              >
            </button>
            <b-icon icon="calendar-today" size="is-small" class="is-left" />
            <b-icon
              v-if="displayValue"
              icon="close-circle"
              class="is-right is-clickable"
              aria-label="Limpar período"
              @click.native.stop.prevent="clear"
            />
          </div>
        </b-tooltip>
      </template>

      <b-dropdown-item custom aria-role="menuitem">
        <div class="date-range-picker__panel">
          <div class="date-range-picker__period">
            <p class="date-range-picker__period-label">Período selecionado</p>
            <p class="date-range-picker__period-text">
              {{ draftDisplayValue || 'Nenhum período selecionado' }}
            </p>
          </div>

          <div class="date-range-picker__body">
            <div class="date-range-picker__calendars">
              <div class="date-range-picker__calendar">
                <div class="date-range-picker__header">
                  <button
                    type="button"
                    class="date-range-picker__nav"
                    aria-label="Mês anterior"
                    @click="shiftLeftMonth(-1)"
                  >
                    <b-icon icon="chevron-left" size="is-small" />
                  </button>
                  <span class="date-range-picker__month-label">{{ leftMonthLabel }}</span>
                  <button
                    type="button"
                    class="date-range-picker__nav"
                    aria-label="Próximo mês"
                    @click="shiftLeftMonth(1)"
                  >
                    <b-icon icon="chevron-right" size="is-small" />
                  </button>
                </div>
                <div class="date-range-picker__weekdays">
                  <span v-for="day in weekdays" :key="`l-${day}`">{{ day }}</span>
                </div>
                <div class="date-range-picker__grid">
                  <button
                    v-for="(cell, index) in leftCells"
                    :key="`l-${index}`"
                    type="button"
                    class="date-range-picker__day"
                    :class="dayClasses(cell)"
                    :disabled="!cell || isDayDisabled(cell)"
                    @click="selectDay(cell)"
                    @mouseenter="hoverDay(cell)"
                  >
                    {{ cell ? cell.getDate() : '' }}
                  </button>
                </div>
              </div>

              <div class="date-range-picker__calendar">
                <div class="date-range-picker__header">
                  <button
                    type="button"
                    class="date-range-picker__nav"
                    aria-label="Mês anterior"
                    :disabled="!canShiftRightBack"
                    @click="shiftRightMonth(-1)"
                  >
                    <b-icon icon="chevron-left" size="is-small" />
                  </button>
                  <span class="date-range-picker__month-label">{{ rightMonthLabel }}</span>
                  <button
                    type="button"
                    class="date-range-picker__nav"
                    aria-label="Próximo mês"
                    @click="shiftRightMonth(1)"
                  >
                    <b-icon icon="chevron-right" size="is-small" />
                  </button>
                </div>
                <div class="date-range-picker__weekdays">
                  <span v-for="day in weekdays" :key="`r-${day}`">{{ day }}</span>
                </div>
                <div class="date-range-picker__grid">
                  <button
                    v-for="(cell, index) in rightCells"
                    :key="`r-${index}`"
                    type="button"
                    class="date-range-picker__day"
                    :class="dayClasses(cell)"
                    :disabled="!cell || isDayDisabled(cell)"
                    @click="selectDay(cell)"
                    @mouseenter="hoverDay(cell)"
                  >
                    {{ cell ? cell.getDate() : '' }}
                  </button>
                </div>
              </div>
            </div>

            <div class="date-range-picker__divider" v-if="showPresets" aria-hidden="true" />

            <aside
              v-if="showPresets"
              class="date-range-picker__presets"
              aria-label="Períodos predefinidos"
            >
              <button
                v-for="preset in presets"
                :key="preset.id"
                type="button"
                class="date-range-picker__preset"
                :class="{ 'is-active': activePreset === preset.id }"
                :disabled="preset.disabled"
                @click.stop.prevent="applyPreset(preset.id)"
              >
                {{ preset.label }}
              </button>
            </aside>
          </div>

          <div class="date-range-picker__footer">
            <button
              type="button"
              class="button is-outlined date-range-picker__cancel"
              @click.stop.prevent="cancel"
            >
              Cancelar
            </button>
            <button
              type="button"
              class="button is-primary date-range-picker__confirm"
              :disabled="!canConfirm"
              @click.stop.prevent="confirm"
            >
              Confirmar
            </button>
          </div>
        </div>
      </b-dropdown-item>
    </b-dropdown>
  </b-field>
</template>

<script>
const WEEKDAYS = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];

const PRESET_IDS = {
  TODAY: 'today',
  THIS_WEEK: 'this_week',
  LAST_WEEK: 'last_week',
  NEXT_WEEK: 'next_week',
  THIS_MONTH: 'this_month',
  LAST_MONTH: 'last_month',
  NEXT_MONTH: 'next_month',
  CUSTOM: 'custom',
};

const startOfDay = date => {
  const next = new Date(date);
  next.setHours(0, 0, 0, 0);
  return next;
};

const toDate = value => {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : startOfDay(value);
  }
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime()) ? null : startOfDay(date);
};

const sameDay = (a, b) =>
  a &&
  b &&
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const addMonths = (year, month, delta) => {
  const date = new Date(year, month + delta, 1);
  return { year: date.getFullYear(), month: date.getMonth() };
};

const monthKey = ({ year, month }) => year * 12 + month;

const formatDisplay = value => {
  const date = toDate(value);
  return date ? date.toLocaleDateString('pt-BR') : '';
};

const monthLabel = ({ year, month }) => {
  const label = new Date(year, month, 1).toLocaleDateString('pt-BR', {
    month: 'long',
    year: 'numeric',
  });
  return label.charAt(0).toUpperCase() + label.slice(1);
};

const buildCells = ({ year, month }) => {
  const first = new Date(year, month, 1);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const offset = first.getDay();
  const cells = [];

  for (let i = 0; i < offset; i += 1) cells.push(null);
  for (let day = 1; day <= daysInMonth; day += 1) {
    cells.push(startOfDay(new Date(year, month, day)));
  }
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
};

const startOfWeek = date => {
  const next = startOfDay(date);
  next.setDate(next.getDate() - next.getDay());
  return next;
};

const endOfWeek = date => {
  const next = startOfWeek(date);
  next.setDate(next.getDate() + 6);
  return next;
};

const startOfMonth = date => startOfDay(new Date(date.getFullYear(), date.getMonth(), 1));

const endOfMonth = date => startOfDay(new Date(date.getFullYear(), date.getMonth() + 1, 0));

/** Soma meses civis preservando o dia quando possível (ex.: 31 jan + 1 mês → 28/29 fev). */
const addMonthsToDate = (date, months) => {
  const year = date.getFullYear();
  const monthIndex = date.getMonth();
  const day = date.getDate();
  const targetMonthIndex = monthIndex + months;
  const targetYear = year + Math.floor(targetMonthIndex / 12);
  const normalizedMonth = ((targetMonthIndex % 12) + 12) % 12;
  const lastDayOfTargetMonth = new Date(targetYear, normalizedMonth + 1, 0).getDate();
  const targetDay = Math.min(day, lastDayOfTargetMonth);
  return startOfDay(new Date(targetYear, normalizedMonth, targetDay));
};

const monthsSpanExceeded = (start, end, maxMonths) => {
  if (!start || !end || maxMonths == null) return false;
  const earlier = start <= end ? start : end;
  const later = start <= end ? end : start;
  const limite = addMonthsToDate(earlier, maxMonths);
  return later.getTime() > limite.getTime();
};

export default {
  name: 'DateRangePicker',
  props: {
    value: { type: Object, default: () => ({ start: null, end: null }) },
    label: { type: String, default: '' },
    placeholder: { type: String, default: 'Selecionar período' },
    /**
     * Limite de seleção relativa a hoje:
     * - fromToday: hoje e datas futuras
     * - untilToday: datas passadas até hoje
     * - any: sem restrição
     */
    dateLimit: {
      type: String,
      default: 'any',
      validator: value => ['any', 'fromToday', 'untilToday'].includes(value),
    },
    /**
     * Alinhamento horizontal do painel:
     * - left: ancora à esquerda do campo (abre para a direita)
     * - right: ancora à direita do campo (abre para a esquerda)
     */
    dropdownAlign: {
      type: String,
      default: 'left',
      validator: value => ['left', 'right'].includes(value),
    },
    /** Exibe atalhos (Hoje, Esta semana, Este mês, etc.). */
    showPresets: {
      type: Boolean,
      default: true,
    },
    /**
     * Máximo de meses de calendário entre início e fim (null = sem limite).
     * Com início já escolhido, datas fora dessa janela ficam desabilitadas.
     */
    maxSpanMonths: {
      type: Number,
      default: null,
      validator: value => value == null || (Number.isFinite(value) && value > 0),
    },
  },
  data() {
    const start = toDate(this.value && this.value.start);
    const today = startOfDay(new Date());
    let left;
    if (start) {
      left = { year: start.getFullYear(), month: start.getMonth() };
    } else if (this.dateLimit === 'untilToday') {
      left = addMonths(today.getFullYear(), today.getMonth(), -1);
    } else {
      left = { year: today.getFullYear(), month: today.getMonth() };
    }
    return {
      leftMonth: left,
      rightMonth: addMonths(left.year, left.month, 1),
      draftStart: toDate(this.value && this.value.start),
      draftEnd: toDate(this.value && this.value.end),
      hoverDate: null,
      activePreset: PRESET_IDS.CUSTOM,
      weekdays: WEEKDAYS,
      triggerHint: null,
    };
  },
  computed: {
    dropdownPosition() {
      // Buefy: is-bottom-right = left:0 (alinha à esquerda);
      // is-bottom-left = right:0 (alinha à direita).
      return this.dropdownAlign === 'right' ? 'is-bottom-left' : 'is-bottom-right';
    },
    today() {
      return startOfDay(new Date());
    },
    displayValue() {
      const start = formatDisplay(this.value.start);
      const end = formatDisplay(this.value.end);
      if (start && end) return `${start} a ${end}`;
      return start || end;
    },
    draftDisplayValue() {
      const start = formatDisplay(this.draftStart);
      const end = formatDisplay(this.draftEnd);
      if (start && end) return `${start} a ${end}`;
      return start || end;
    },
    leftMonthLabel() {
      return monthLabel(this.leftMonth);
    },
    rightMonthLabel() {
      return monthLabel(this.rightMonth);
    },
    leftCells() {
      return buildCells(this.leftMonth);
    },
    rightCells() {
      return buildCells(this.rightMonth);
    },
    canShiftRightBack() {
      return monthKey(this.rightMonth) > monthKey(this.leftMonth) + 1;
    },
    previewEnd() {
      if (this.draftStart && !this.draftEnd && this.hoverDate) return this.hoverDate;
      return this.draftEnd;
    },
    canConfirm() {
      return Boolean(this.draftStart && this.draftEnd);
    },
    presets() {
      const weekPreset =
        this.dateLimit === 'fromToday'
          ? { id: PRESET_IDS.NEXT_WEEK, label: 'Próxima semana' }
          : { id: PRESET_IDS.LAST_WEEK, label: 'Semana passada' };
      const monthPreset =
        this.dateLimit === 'fromToday'
          ? { id: PRESET_IDS.NEXT_MONTH, label: 'Próximo mês' }
          : { id: PRESET_IDS.LAST_MONTH, label: 'Mês passado' };

      return [
        { id: PRESET_IDS.TODAY, label: 'Hoje' },
        { id: PRESET_IDS.THIS_WEEK, label: 'Esta semana' },
        weekPreset,
        { id: PRESET_IDS.THIS_MONTH, label: 'Este mês' },
        monthPreset,
        { id: PRESET_IDS.CUSTOM, label: 'Período customizado' },
      ].map(preset => ({
        ...preset,
        disabled: preset.id !== PRESET_IDS.CUSTOM && !this.resolvePresetRange(preset.id),
      }));
    },
  },
  watch: {
    displayValue() {
      this.triggerHint = null;
      this.$nextTick(this.refreshTriggerHint);
    },
  },
  mounted() {
    this.$nextTick(this.refreshTriggerHint);
    this.onWindowResize = () => this.refreshTriggerHint();
    window.addEventListener('resize', this.onWindowResize);
  },
  beforeDestroy() {
    if (this.onWindowResize) {
      window.removeEventListener('resize', this.onWindowResize);
    }
  },
  methods: {
    refreshTriggerHint() {
      if (!this.displayValue) {
        this.triggerHint = null;
        return;
      }
      const label = this.$refs.triggerLabel;
      if (!label) {
        this.triggerHint = null;
        return;
      }
      const isTruncated = label.scrollWidth > label.clientWidth + 1;
      this.triggerHint = isTruncated ? this.displayValue : null;
    },
    isDayDisabled(day) {
      if (!day) return true;
      const date = startOfDay(day);
      if (this.dateLimit === 'fromToday' && date < this.today) return true;
      if (this.dateLimit === 'untilToday' && date > this.today) return true;

      // Com início escolhido e fim pendente, restringe ao span máximo.
      if (
        this.maxSpanMonths != null &&
        this.draftStart &&
        !this.draftEnd &&
        monthsSpanExceeded(this.draftStart, date, this.maxSpanMonths)
      ) {
        return true;
      }

      return false;
    },
    clampRange(start, end) {
      if (!start || !end) return null;
      let nextStart = startOfDay(start);
      let nextEnd = startOfDay(end);
      if (nextEnd < nextStart) {
        const swap = nextStart;
        nextStart = nextEnd;
        nextEnd = swap;
      }

      if (this.dateLimit === 'untilToday') {
        if (nextStart > this.today) return null;
        if (nextEnd > this.today) nextEnd = this.today;
      }
      if (this.dateLimit === 'fromToday') {
        if (nextEnd < this.today) return null;
        if (nextStart < this.today) nextStart = this.today;
      }

      if (
        this.maxSpanMonths != null &&
        monthsSpanExceeded(nextStart, nextEnd, this.maxSpanMonths)
      ) {
        nextEnd = addMonthsToDate(nextStart, this.maxSpanMonths);
      }

      return { start: nextStart, end: nextEnd };
    },
    resolvePresetRange(presetId) {
      const { today } = this;
      if (presetId === PRESET_IDS.TODAY) {
        return this.clampRange(today, today);
      }
      if (presetId === PRESET_IDS.THIS_WEEK) {
        return this.clampRange(startOfWeek(today), endOfWeek(today));
      }
      if (presetId === PRESET_IDS.LAST_WEEK) {
        const lastWeekRef = startOfDay(today);
        lastWeekRef.setDate(lastWeekRef.getDate() - 7);
        return this.clampRange(startOfWeek(lastWeekRef), endOfWeek(lastWeekRef));
      }
      if (presetId === PRESET_IDS.NEXT_WEEK) {
        const nextWeekRef = startOfDay(today);
        nextWeekRef.setDate(nextWeekRef.getDate() + 7);
        return this.clampRange(startOfWeek(nextWeekRef), endOfWeek(nextWeekRef));
      }
      if (presetId === PRESET_IDS.THIS_MONTH) {
        return this.clampRange(startOfMonth(today), endOfMonth(today));
      }
      if (presetId === PRESET_IDS.LAST_MONTH) {
        const lastMonthRef = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        return this.clampRange(startOfMonth(lastMonthRef), endOfMonth(lastMonthRef));
      }
      if (presetId === PRESET_IDS.NEXT_MONTH) {
        const nextMonthRef = new Date(today.getFullYear(), today.getMonth() + 1, 1);
        return this.clampRange(startOfMonth(nextMonthRef), endOfMonth(nextMonthRef));
      }
      return null;
    },
    applyPreset(presetId) {
      if (presetId === PRESET_IDS.CUSTOM) {
        this.activePreset = PRESET_IDS.CUSTOM;
        return;
      }
      const range = this.resolvePresetRange(presetId);
      if (!range) return;
      this.draftStart = range.start;
      this.draftEnd = range.end;
      this.hoverDate = null;
      this.activePreset = presetId;
      this.syncMonthsFromDraft();
    },
    onActiveChange(open) {
      if (open) {
        this.beginEditing();
      } else {
        this.hoverDate = null;
      }
    },
    closeDropdown() {
      if (this.$refs.dropdown) {
        this.$refs.dropdown.isActive = false;
      }
    },
    beginEditing() {
      this.draftStart = toDate(this.value.start);
      this.draftEnd = toDate(this.value.end);
      this.hoverDate = null;
      this.activePreset = PRESET_IDS.CUSTOM;
      this.syncMonthsFromDraft();
    },
    syncMonthsFromDraft() {
      const { draftStart: start, draftEnd: end } = this;
      if (start) {
        this.leftMonth = { year: start.getFullYear(), month: start.getMonth() };
        if (end) {
          const endMonth = { year: end.getFullYear(), month: end.getMonth() };
          this.rightMonth =
            monthKey(endMonth) > monthKey(this.leftMonth)
              ? endMonth
              : addMonths(this.leftMonth.year, this.leftMonth.month, 1);
        } else {
          this.rightMonth = addMonths(this.leftMonth.year, this.leftMonth.month, 1);
        }
      } else if (this.dateLimit === 'untilToday') {
        const { today } = this;
        this.leftMonth = addMonths(today.getFullYear(), today.getMonth(), -1);
        this.rightMonth = { year: today.getFullYear(), month: today.getMonth() };
      } else {
        const { today } = this;
        this.leftMonth = { year: today.getFullYear(), month: today.getMonth() };
        this.rightMonth = addMonths(today.getFullYear(), today.getMonth(), 1);
      }
      this.ensureRightAfterLeft();
    },
    ensureRightAfterLeft() {
      if (monthKey(this.rightMonth) <= monthKey(this.leftMonth)) {
        this.rightMonth = addMonths(this.leftMonth.year, this.leftMonth.month, 1);
      }
    },
    shiftLeftMonth(delta) {
      this.leftMonth = addMonths(this.leftMonth.year, this.leftMonth.month, delta);
      this.ensureRightAfterLeft();
      if (this.activePreset === PRESET_IDS.SELECT_MONTH) {
        this.applyPreset(PRESET_IDS.SELECT_MONTH);
      }
    },
    shiftRightMonth(delta) {
      if (delta < 0 && !this.canShiftRightBack) return;
      const next = addMonths(this.rightMonth.year, this.rightMonth.month, delta);
      if (monthKey(next) < monthKey(this.leftMonth) + 1) return;
      this.rightMonth = next;
    },
    selectDay(day) {
      if (!day || this.isDayDisabled(day)) return;
      const selected = startOfDay(day);
      this.activePreset = PRESET_IDS.CUSTOM;

      if (!this.draftStart || (this.draftStart && this.draftEnd)) {
        this.draftStart = selected;
        this.draftEnd = null;
        this.hoverDate = null;
        return;
      }

      if (monthsSpanExceeded(this.draftStart, selected, this.maxSpanMonths)) {
        return;
      }

      if (selected < this.draftStart) {
        this.draftEnd = this.draftStart;
        this.draftStart = selected;
      } else {
        this.draftEnd = selected;
      }
      this.hoverDate = null;
    },
    hoverDay(day) {
      if (!day || this.isDayDisabled(day) || !this.draftStart || this.draftEnd) {
        this.hoverDate = null;
        return;
      }
      this.hoverDate = startOfDay(day);
    },
    dayClasses(day) {
      if (!day) return { 'is-empty': true };

      if (this.isDayDisabled(day)) {
        return {
          'is-disabled': true,
          'is-today': sameDay(day, this.today),
        };
      }

      const start = this.draftStart;
      const end = this.previewEnd;
      const ordered = start && end && end < start ? { start: end, end: start } : { start, end };

      const isStart = ordered.start && sameDay(day, ordered.start);
      const isEnd = ordered.end && sameDay(day, ordered.end);
      const inRange = ordered.start && ordered.end && day > ordered.start && day < ordered.end;

      return {
        'is-today': sameDay(day, this.today),
        'is-selected': isStart || isEnd,
        'is-range-start': isStart && ordered.end,
        'is-range-end': isEnd && ordered.start,
        'is-in-range': inRange,
        'is-preview': Boolean(
          this.draftStart && !this.draftEnd && this.hoverDate && (isEnd || inRange),
        ),
      };
    },
    cancel() {
      this.hoverDate = null;
      this.closeDropdown();
    },
    clear() {
      this.draftStart = null;
      this.draftEnd = null;
      this.hoverDate = null;
      this.activePreset = PRESET_IDS.CUSTOM;
      this.$emit('input', { start: null, end: null });
      this.closeDropdown();
    },
    confirm() {
      if (!this.canConfirm) return;
      const clamped = this.clampRange(this.draftStart, this.draftEnd);
      if (!clamped) return;
      this.draftStart = clamped.start;
      this.draftEnd = clamped.end;
      this.$emit('input', { start: this.draftStart, end: this.draftEnd });
      this.hoverDate = null;
      this.closeDropdown();
    },
  },
};
</script>

<style lang="scss" scoped>
// Mesmo valor de $primary em App.vue — sem partial de variáveis compartilhado entre SFCs.
$primary: #2153b5;

.date-range-picker {
  width: 100%;

  ::v-deep .dropdown {
    width: 100%;
  }

  ::v-deep .dropdown-menu {
    max-width: none;
    min-width: 48rem;
    width: max-content;
  }

  ::v-deep .dropdown-content {
    max-width: none;
    min-width: 48rem;
    overflow: visible;
    width: max-content;
  }

  ::v-deep .dropdown-item {
    padding: 0;
    width: auto;
  }

  // Sem coluna de presets: encolhe o painel aos dois calendários.
  &--no-presets {
    ::v-deep .dropdown-menu,
    ::v-deep .dropdown-content {
      min-width: 36.5rem;
    }

    .date-range-picker__panel {
      min-width: 36.5rem;
    }
  }
}

.date-range-picker__tooltip {
  display: block;
  width: 100%;

  ::v-deep .tooltip-trigger {
    display: block;
    width: 100%;
  }
}

.date-range-picker__control {
  width: 100%;

  // Mantém a cor do ícone ao abrir/focar; escurece só no hover do ícone.
  ::v-deep .icon.is-right {
    color: #dbdbdb;
  }

  ::v-deep .icon.is-right.is-clickable:hover {
    color: #7a7a7a;
  }
}

.date-range-picker__control.has-icons-left .date-range-picker__trigger {
  padding-left: 2.5em;
}

.date-range-picker__control.has-icons-right .date-range-picker__trigger {
  padding-right: 2.5em;
}

.date-range-picker__trigger {
  justify-content: flex-start;
  text-align: left;
  width: 100%;
}

.date-range-picker__trigger-label {
  display: block;
  overflow: hidden;
  text-align: left;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;

  &.is-placeholder {
    color: #b5b5b5;
  }
}

.date-range-picker__panel {
  box-sizing: border-box;
  min-width: 48rem;
  padding: 0.75rem;
  width: max-content;
}

.date-range-picker__period {
  margin-bottom: 0.75rem;
}

.date-range-picker__period-label {
  color: #363636;
  font-size: 0.85rem;
  font-weight: 700;
  margin-bottom: 0.2rem;
}

.date-range-picker__period-text {
  color: #363636;
  font-size: 0.85rem;
  margin: 0;
}

.date-range-picker__body {
  display: flex;
  flex-wrap: nowrap;
  gap: 0.75rem;
}

.date-range-picker__calendars {
  display: flex;
  flex-wrap: nowrap;
  gap: 1.25rem;
}

.date-range-picker__calendar {
  width: 17rem;
}

.date-range-picker__divider {
  background: #dbdbdb;
  flex: 0 0 1px;
  width: 1px;
}

.date-range-picker__presets {
  display: flex;
  flex: 0 0 9.5rem;
  flex-direction: column;
  gap: 0.125rem;
  width: 9.5rem;
}

.date-range-picker__preset {
  background: transparent;
  border: 0;
  border-radius: 4px;
  color: #363636;
  cursor: pointer;
  font-size: 0.75rem;
  min-height: 2rem;
  padding: 0.5rem 0.625rem;
  text-align: left;
  width: 100%;

  &:hover:not(:disabled):not(.is-active) {
    background: #f5f5f5;
  }

  &.is-active {
    background: #e8f0f8;
    color: $primary;
    font-weight: 700;
  }

  &:disabled {
    color: #c0c0c0;
    cursor: default;
  }
}

.date-range-picker__header {
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
}

.date-range-picker__month-label {
  color: #363636;
  font-size: 0.95rem;
  font-weight: 600;
  text-transform: capitalize;
}

.date-range-picker__nav {
  align-items: center;
  background: transparent;
  border: 0;
  border-radius: 4px;
  color: $primary;
  cursor: pointer;
  display: inline-flex;
  height: 1.75rem;
  justify-content: center;
  width: 1.75rem;

  &:disabled {
    color: #dbdbdb;
    cursor: default;
  }

  &:not(:disabled):hover {
    background: #f5f5f5;
  }
}

.date-range-picker__weekdays,
.date-range-picker__grid {
  display: grid;
  gap: 0;
  grid-template-columns: repeat(7, 1fr);
}

.date-range-picker__weekdays {
  margin-bottom: 0.25rem;

  span {
    color: #7a7a7a;
    font-size: 0.75rem;
    font-weight: 600;
    padding: 0.25rem 0;
    text-align: center;
  }
}

.date-range-picker__day {
  align-items: center;
  background: transparent;
  border: 0;
  color: #363636;
  cursor: pointer;
  display: inline-flex;
  font-size: 0.85rem;
  height: 2rem;
  justify-content: center;
  position: relative;
  width: 100%;

  &.is-empty {
    cursor: default;
  }

  &.is-disabled {
    color: #c0c0c0;
    cursor: default;

    &:hover {
      background: transparent;
    }
  }

  &:not(.is-empty):not(.is-disabled):hover {
    background: #eff5fb;
  }

  &.is-today:not(.is-selected) {
    box-shadow: inset 0 0 0 1px rgba($primary, 0.45);
  }

  &.is-in-range,
  &.is-preview {
    background: rgba($primary, 0.12);
    border-radius: 0;
  }

  &.is-selected {
    background: $primary;
    border-radius: 4px;
    color: #fff;
    font-weight: 600;
  }

  &.is-range-start {
    border-radius: 4px 0 0 4px;
  }

  &.is-range-end {
    border-radius: 0 4px 4px 0;
  }

  &.is-range-start.is-range-end {
    border-radius: 4px;
  }
}

.date-range-picker__footer {
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
  margin-top: 0.75rem;
}

.date-range-picker__cancel {
  border-color: $primary;
  color: $primary;
}

.date-range-picker__confirm {
  background-color: $primary;
  border-color: $primary;
}

@media (max-width: 1023px) {
  .date-range-picker {
    ::v-deep .dropdown-menu,
    ::v-deep .dropdown-content {
      max-width: calc(100vw - 1.5rem);
      min-width: 0;
      width: min(22rem, calc(100vw - 1.5rem));
    }

    ::v-deep .dropdown-item {
      width: 100%;
    }

    &--no-presets {
      ::v-deep .dropdown-menu,
      ::v-deep .dropdown-content {
        min-width: 0;
      }

      .date-range-picker__panel {
        min-width: 0;
      }
    }
  }

  .date-range-picker__panel {
    box-sizing: border-box;
    max-width: 100%;
    min-width: 0;
    width: 100%;
  }

  .date-range-picker__body {
    flex-direction: column;
  }

  .date-range-picker__calendars {
    flex-direction: column;
  }

  .date-range-picker__calendar {
    width: 100%;
  }

  .date-range-picker__divider {
    height: 1px;
    width: 100%;
  }

  .date-range-picker__presets {
    flex-basis: auto;
    width: 100%;
  }
}

// Coluna única (mobile): calendário usa quase toda a largura da tela.
@media (max-width: 768px) {
  .date-range-picker {
    ::v-deep .dropdown-menu,
    ::v-deep .dropdown-content {
      left: 0.75rem !important;
      max-width: calc(100vw - 1.5rem);
      min-width: 0;
      right: 0.75rem !important;
      width: calc(100vw - 1.5rem);
    }
  }

  .date-range-picker__panel {
    max-width: 100%;
    min-width: 0;
    width: 100%;
  }
}
</style>
